import { useMemo, useState, type RefObject } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { GLAWithIcon } from "../../../general/GLAWithIcon";
import { SubledgerSelector, type Subledger } from "../../SubledgerSelector";
import type { GlAccount } from "../types";
import { filterBySearchQuery, getApplicableLegalEntityTag, groupAccounts } from "../utils";

interface GLASelectorMenuProps {
    searchInputRef: RefObject<HTMLInputElement | null>;
    accounts: GlAccount[];
    onSelect: (account: GlAccount) => void;
    hasActiveFilter: boolean;
    showingAll: boolean;
    onToggleShowAll: () => void;
    subledgers?: Subledger[];
    selectedSubledger?: Subledger | null;
    onSubledgerChange?: (subledger: Subledger | null) => void;
    onCreateSubledger?: (name: string) => void;
}

export function GLASelectorMenu({
    searchInputRef,
    accounts,
    onSelect,
    hasActiveFilter,
    showingAll,
    onToggleShowAll,
    subledgers,
    selectedSubledger,
    onSubledgerChange,
    onCreateSubledger,
}: GLASelectorMenuProps) {
    const subledgerSection =
        subledgers !== undefined && onSubledgerChange !== undefined && onCreateSubledger !== undefined
            ? { subledgers, onSubledgerChange, onCreateSubledger }
            : null;
    const [searchQuery, setSearchQuery] = useState("");

    const groups = useMemo(() => {
        const searched = filterBySearchQuery(accounts, searchQuery);
        return groupAccounts(searched);
    }, [accounts, searchQuery]);

    const visibleCount = groups.reduce((sum, g) => sum + g.accounts.length, 0);

    return (
        <div className="flex h-96 w-[max(calc(var(--button-width)*1.3),24rem)] flex-col overflow-hidden rounded-[7px] border border-gray-200 bg-white shadow-lg">
            <div className="flex h-[48px] shrink-0 items-center gap-2 rounded-t-[7px] border-b border-gray-200 bg-gray-100 px-4">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <MagnifyingGlassIcon className="size-5 shrink-0 text-gray-400" aria-hidden="true" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        className="min-h-0 min-w-[4rem] flex-1 border-none bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
                        placeholder="Zoeken"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <span className="shrink-0 text-sm font-normal leading-none text-gray-700">
                    {visibleCount} rekeningen
                </span>
                {hasActiveFilter && (
                    <button
                        type="button"
                        onClick={onToggleShowAll}
                        className="shrink-0 text-sm font-normal leading-none text-[color:var(--primary)] hover:text-darkprimary"
                    >
                        {showingAll ? "suggesties" : "alles tonen"}
                    </button>
                )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {groups.map((group) => (
                    <div key={group.type}>
                        <div className="mx-3 border-b-[0.5px] border-b-[rgba(156,163,175,0.11)] pb-1 pt-3">
                            <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                {group.label}
                            </span>
                        </div>
                        {group.accounts.map((account) => {
                            const tag = getApplicableLegalEntityTag(account);
                            return (
                                <button
                                    key={account.id}
                                    type="button"
                                    onClick={() => onSelect(account)}
                                    className="flex w-full items-center px-3 py-2 text-left hover:bg-gray-50"
                                >
                                    <GLAWithIcon
                                        icon={account.icon}
                                        label={account.label_nl}
                                        iconSize="sm"
                                        labelClassName="whitespace-normal text-sm leading-snug"
                                        wrapperClassName="min-w-0 flex-1"
                                        suffix={
                                            tag ? (
                                                <span className="ml-1.5 inline-flex align-middle rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.04em] text-gray-500">
                                                    {tag}
                                                </span>
                                            ) : undefined
                                        }
                                    />
                                </button>
                            );
                        })}
                    </div>
                ))}

                {visibleCount === 0 && (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">
                        Geen rekeningen gevonden
                    </div>
                )}
            </div>

            {subledgerSection && (
                <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-3">
                    <SubledgerSelector
                        subledgers={subledgerSection.subledgers}
                        value={selectedSubledger ?? null}
                        onChange={subledgerSection.onSubledgerChange}
                        onCreate={subledgerSection.onCreateSubledger}
                        optionsPlacement="top"
                    />
                </div>
            )}
        </div>
    );
}
